import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Product, ProductSize } from "@/types/product";

/**
 * Filter options for product queries
 * @property category - Filter by product category
 * @property minPrice - Minimum price filter
 * @property maxPrice - Maximum price filter
 * @property search - Search term for product title
 * @property isAvailable - Filter by product availability
 */
export interface ProductFilters {
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  search?: string;
  isAvailable?: boolean;
}

/**
 * Sort configuration for product queries
 * @property field - The field to sort by
 * @property direction - Sort direction (ascending or descending)
 */
interface SortConfig {
  field: keyof Product;
  direction: "asc" | "desc";
}

/**
 * Props for the useProducts hook
 * @property storeId - ID of the store to manage products for
 * @property filters - Optional filters to apply to product queries
 * @property sort - Optional sorting configuration
 * @property enabled - Whether the query should be enabled
 */
interface UseProductsProps {
  storeId: string;
  filters?: ProductFilters;
  sort?: SortConfig;
  enabled?: boolean;
}

/**
 * Input type for product size data
 * @property size_name - Name of the size option
 * @property price - Price for this size
 * @property image_url - Optional image URL for this size
 * @property use_product_image - Whether to use the main product image
 */
interface ProductSizeInput {
  size_name: string;
  price: number;
  image_url?: string | null;
  use_product_image?: boolean;
}

/**
 * Input type for creating a new product
 * @property title - Product title
 * @property description - Product description
 * @property category - Product category
 * @property active - Whether the product is active
 * @property image_url - Optional main product image URL
 * @property occasions - Optional array of occasions
 * @property sizes - Array of size options
 */
interface CreateProductInput {
  title: string;
  description: string;
  category: string;
  active: boolean;
  image_url?: string | null;
  occasions?: { id: string; name: string; }[];
  sizes: ProductSizeInput[];
}

/**
 * Input type for updating an existing product
 * Extends CreateProductInput but makes all fields optional except id
 * @property id - Product ID (required)
 * @property sizes - Optional array of size options with optional IDs
 */
interface UpdateProductInput extends Partial<Omit<CreateProductInput, 'sizes'>> {
  id: string;
  sizes?: (ProductSizeInput & { id?: string; })[];
}

/**
 * Custom hook for managing products in a store
 * 
 * Features:
 * - Fetch products with filtering and sorting
 * - Create new products with sizes and occasions
 * - Update existing products and their variants
 * - Delete products (cascades to sizes and occasions)
 * - Manage product sizes and occasions
 * 
 * @param storeId - ID of the store to manage products for
 * @param filters - Optional filters to apply to product queries
 * @param sort - Optional sorting configuration
 * @param enabled - Whether the query should be enabled
 * @returns Object containing products data and CRUD operations
 */
export function useProducts({ storeId, filters, sort, enabled = true }: UseProductsProps) {
  const queryClient = useQueryClient();

  // Main products query with relationships
  const {
    data: products = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["products", storeId, filters, sort],
    queryFn: async () => {
      // For florist dashboard, show products if store exists and is not rejected by admin.
      // Florist's own store_status (active/inactive) or product.active status will determine visibility in their list if they want that.
      const { data: store } = await supabase
        .from("store_profiles")
        .select("id, approval_status") // Only need approval_status for this check
        .eq("id", storeId)
        .single();

      if (!store || store.approval_status === 'rejected') {
        // If store doesn't exist or is rejected, don't show products in florist dashboard product list
        return []; 
      }

      // Build query with relationships
      const query = supabase
        .from("products")
        .select(
          `
          *,
          sizes:product_sizes(
            id,
            size_name,
            price,
            main_image_url,
            gallery_image_urls,
            use_main_product_image,
            is_active
          ),
          occasions:product_occasions(
            occasions(
              id,
              name
            )
          )
        `
        )
        .eq("store_id", storeId);

      // Apply filters if provided
      if (filters?.category) query.eq("category", filters.category);
      if (filters?.minPrice) query.gte("price", filters.minPrice);
      if (filters?.maxPrice) query.lte("price", filters.maxPrice);
      if (filters?.search) query.ilike("title", `%${filters.search}%`);
      if (filters?.isAvailable !== undefined) query.eq("active", filters.isAvailable);

      // Apply sorting
      query.order(sort?.field || "created_at", { 
        ascending: sort?.direction === "asc" || false 
      });

      const { data, error } = await query;
      if (error) throw error;

      // Transform response data
      return data.map((product: any) => ({
        ...product,
        // Map main_image_url to image_url for the main product
        image_url: product.main_image_url,
        sizes: (product.sizes || []).map((size: any) => ({
          id: size.id,
          size_name: size.size_name,
          price: size.price,
          image_url: size.main_image_url || null,
          use_product_image: size.use_main_product_image || false
        })),
        occasions: product.occasions?.map((o: any) => o.occasions) || []
      }));
    },
    enabled: enabled && !!storeId, // This enabled flag is for the hook consumer, auth checks are now in page components
  });

  const createProductMutation = useMutation({
    mutationFn: async (input: CreateProductInput) => {
      const { data: store } = await supabase
        .from("store_profiles")
        .select("id, approval_status") // Only need approval_status
        .eq("id", storeId)
        .single();

      if (!store) {
        throw new Error("Store profile not found. Cannot create product.");
      }
      if (store.approval_status === 'rejected') {
        throw new Error("This store has been rejected and cannot add products.");
      }
      // Product can be created even if store is pending approval or inactive by florist's choice
      // Create the product
      const { data: product, error: productError } = await supabase
        .from("products")
        .insert({
          title: input.title,
          description: input.description,
          category: input.category,
          active: input.active,
          store_id: store.id,
          price: input.sizes[0].price, // Base price from first size
          main_image_url: input.image_url || input.sizes[0].image_url || null,
          gallery_image_urls: [] // Assuming gallery can be added later via update
        })
        .select()
        .single();

      if (productError) throw productError;
      if (!product) throw new Error("Product creation failed to return data.");

      console.log("Created product:", product.id, "with main image:", product.main_image_url);

      // Create the sizes
      const sizesData = input.sizes.map(size => {
        let sizeImageUrl = null;
        if (size.use_product_image === false && size.image_url) {
          sizeImageUrl = size.image_url;
        }
        console.log(`Size ${size.size_name}: use_main_product_image=${size.use_product_image}, main_image_url=${sizeImageUrl}`);
        return {
          product_id: product.id,
          size_name: size.size_name,
          price: size.price,
          main_image_url: sizeImageUrl, // Use null if using the product image
          gallery_image_urls: [],
          use_main_product_image: !!size.use_product_image,
          is_active: true
        };
      });

      if (sizesData.length > 0) {
        const { error: sizesError } = await supabase
            .from("product_sizes")
            .insert(sizesData);
        if (sizesError) {
            console.error("Error inserting sizes:", sizesError);
            throw sizesError;
        }
      }

      // Create occasions if any
      if (input.occasions?.length) {
        const { error: occasionsError } = await supabase
          .from("product_occasions")
          .insert(
            input.occasions.map(occasion => ({
              product_id: product.id,
              occasion_id: occasion.id
            }))
          );
        if (occasionsError) throw occasionsError;
      }
      return product;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["products", storeId] });
      toast.success(`Product "${data.title}" created successfully!`);
    },
    onError: (error: any) => {
      console.error("Error creating product:", error);
      toast.error(error.message || "Failed to create product");
    },
  });

  /**
   * Updates an existing product, its sizes, and occasions
   * @throws Error if validation fails or update fails
   */
  const updateProduct = async (input: UpdateProductInput) => {
    if (!input.id) throw new Error("Product ID is required for update");
    const { data: store } = await supabase
      .from("store_profiles")
      .select("id, approval_status") // Only need approval_status
      .eq("id", storeId)
      .single();
    if (!store) throw new Error("Store profile not found. Cannot update product.");
    if (store.approval_status === 'rejected') {
      throw new Error("This store has been rejected and cannot update products.");
    }
    // Validate sizes if provided
    if (input.sizes?.length) {
      const invalidSizes = input.sizes.filter(
        size => !size.size_name || typeof size.price !== 'number' || size.price <= 0
      );
      
      if (invalidSizes.length) {
        throw new Error("All product sizes must have a name and valid price");
      }
      
      // Image upload is optional - products can be saved without images
    }

    // Update product details
    const { error: productError } = await supabase
      .from("products")
      .update({
        title: input.title,
        description: input.description,
        category: input.category,
        active: input.active,
        price: input.sizes?.[0]?.price || 0,
        main_image_url: input.image_url || input.sizes?.[0]?.image_url || null
      })
      .eq("id", input.id);

    if (productError) throw productError;

    // Update sizes if provided
    if (input.sizes?.length) {
      const { data: existingSizes } = await supabase
        .from("product_sizes")
        .select("id")
        .eq("product_id", input.id);

      const existingSizeIds = new Set(existingSizes?.map(size => size.id) || []);
      
      // PRODUCT SIZES UPDATE FIX
      // Previously used upsert which had issues with null/undefined id fields
      // Now we separate existing and new sizes to handle them differently
      
      // Get existing sizes that need updating (those with valid IDs)
      const existingSizesToUpdate = input.sizes
        .filter(size => size.id && existingSizeIds.has(size.id))
        .map(size => {
          let sizeImageUrl = null;
          if (size.use_product_image === false && size.image_url) {
            sizeImageUrl = size.image_url;
          }
          
          return {
            id: size.id,
            product_id: input.id,
            size_name: size.size_name,
            price: size.price,
            main_image_url: sizeImageUrl,
            use_main_product_image: !!size.use_product_image,
            is_active: true
          };
        });
        
      // Get new sizes that need inserting (those without IDs or with invalid IDs)
      // For these we omit the ID field completely to let PostgreSQL generate them
      const newSizesToInsert = input.sizes
        .filter(size => !size.id || !existingSizeIds.has(size.id))
        .map(size => {
          let sizeImageUrl = null;
          if (size.use_product_image === false && size.image_url) {
            sizeImageUrl = size.image_url;
          }
          
          return {
            product_id: input.id,
            size_name: size.size_name,
            price: size.price,
            main_image_url: sizeImageUrl,
            use_main_product_image: !!size.use_product_image,
            is_active: true
          };
        });
      
      // Enhanced logging to debug issues with product sizes
      console.log("Existing sizes to update:", JSON.stringify(existingSizesToUpdate, null, 2));
      console.log("New sizes to insert:", JSON.stringify(newSizesToInsert, null, 2));
      
      // UPDATE: Handle existing sizes individually to ensure ID integrity
      if (existingSizesToUpdate.length > 0) {
        for (const size of existingSizesToUpdate) {
          const { error: updateError } = await supabase
            .from("product_sizes")
            .update({
              size_name: size.size_name,
              price: size.price,
              main_image_url: size.main_image_url,
              use_main_product_image: size.use_main_product_image,
              is_active: size.is_active
            })
            .eq("id", size.id);
            
          if (updateError) {
            console.error(`Error updating size ${size.id}:`, updateError);
            throw updateError;
          }
        }
      }
      
      // INSERT: Add new sizes in a single batch operation, omitting IDs
      if (newSizesToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from("product_sizes")
          .insert(newSizesToInsert);
          
        if (insertError) {
          console.error("Error inserting new sizes:", insertError);
          console.error("Size data that caused error:", JSON.stringify(newSizesToInsert, null, 2));
          throw insertError;
        }
      }

      // DELETE: Remove sizes that are no longer needed
      const sizeIdsToDelete = Array.from(existingSizeIds)
        .filter(id => !input.sizes
          .map(size => size.id)
          .includes(id));
      
      if (sizeIdsToDelete.length) {
        console.log("Sizes to delete:", sizeIdsToDelete);
        const { error: deleteError } = await supabase
          .from("product_sizes")
          .delete()
          .in("id", sizeIdsToDelete);
          
        if (deleteError) throw deleteError;
      }
    }

    // Update occasions if provided
    if (input.occasions?.length) {
      // Delete existing occasions
      await supabase
        .from("product_occasions")
        .delete()
        .eq("product_id", input.id);

      // Insert new occasions
      const occasionsToInsert = input.occasions
        .filter(occasion => occasion.id)
        .map(occasion => ({
          product_id: input.id,
          occasion_id: occasion.id
        }));
        
      if (occasionsToInsert.length) {
        const { error: insertOccasionsError } = await supabase
          .from("product_occasions")
          .insert(occasionsToInsert);

        if (insertOccasionsError) throw insertOccasionsError;
      }
    }

    // Invalidate queries to refetch data
    queryClient.invalidateQueries({ queryKey: ["products"] });
    return { success: true, id: input.id };
  };

  /**
   * Deletes a product and its associated data
   * Cascades to sizes and occasions via foreign key constraints
   */
  const deleteProduct = useMutation({
    mutationFn: async (id: string) => {
      const { data: store } = await supabase
        .from("store_profiles")
        .select("id, approval_status") // Only need approval_status
        .eq("id", storeId)
        .single();
      if (!store) throw new Error("Store profile not found. Cannot delete product.");
      if (store.approval_status === 'rejected') {
        throw new Error("This store has been rejected and cannot delete products.");
      }
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", id)
        .eq("store_id", store.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products", storeId] });
      toast.success("Product deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    },
  });

  return {
    products,
    isLoading,
    error,
    createProduct: createProductMutation,
    updateProduct,
    deleteProduct,
    getProductById: (id: string) => products.find(p => p.id === id)
  };
} 